import type {
  CompetitiveRole,
  Direction,
  MetricObservation,
  MetricUnit,
  Valence,
} from "./types";

const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function cleanZero(value: number): number {
  return Object.is(value, -0) || Math.abs(value) < 1e-10 ? 0 : value;
}

export function formatValue(
  value: number | null | undefined,
  unit: MetricUnit,
  compactCount = false,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "Not available";
  const clean = cleanZero(value);
  switch (unit) {
    case "percent": {
      const percentage = clean * 100;
      return `${percentage.toFixed(Math.abs(percentage) < 1 ? 2 : 1)}%`;
    }
    case "percentage_points":
      return `${clean > 0 ? "+" : ""}${clean.toFixed(1)} pts`;
    case "index":
      return number.format(Math.round(clean));
    case "count":
      return compactCount ? compact.format(clean) : number.format(Math.round(clean));
    case "ratio":
      return clean < 10 ? clean.toFixed(2) : clean.toFixed(1);
    case "months":
      return `${Number.isInteger(clean) ? clean.toFixed(0) : clean.toFixed(1)} mo`;
    case "hours":
      return `${Number.isInteger(clean) ? clean.toFixed(0) : clean.toFixed(1)} hrs`;
    default:
      return String(clean);
  }
}

export function directionFor(delta: number | null | undefined, threshold: number): Direction {
  if (delta === null || delta === undefined || !Number.isFinite(delta) || Math.abs(delta) < threshold) {
    return "flat";
  }
  return delta > 0 ? "up" : "down";
}

export function valenceFor(
  direction: Direction,
  polarity: "higher_is_better" | "lower_is_better" | "neutral",
): Valence {
  if (direction === "flat" || polarity === "neutral") return "neutral";
  if (polarity === "higher_is_better") return direction === "up" ? "positive" : "negative";
  return direction === "down" ? "positive" : "negative";
}

export function metricDisplay(metric: MetricObservation, compactCount = false): string {
  return metric.display_value ?? formatValue(metric.value, metric.unit, compactCount);
}

export function safeRoleLabel(
  role: CompetitiveRole,
  status: string,
): CompetitiveRole {
  if (status !== "supported") return "Evidence Not Yet Sufficient";
  return role;
}

export function formatMonth(month: string): string {
  const [year, monthNumber] = month.split("-").map(Number);
  if (!year || !monthNumber) return month;
  return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(year, monthNumber - 1, 1)),
  );
}
